import React from "react";
import { Box, Typography, Chip, Card, CardContent, InputBase, Paper } from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import type { RequestStatus, Priority, HttpMethod } from "../types";

export const STATUS_CONFIG: Record<
  RequestStatus,
  { label: string; bg: string; text: string; dot: string; isPulsing?: boolean }
> = {
  completed: {
    label: "Completed",
    bg: "#ECFDF5",
    text: "#047857",
    dot: "#10B981",
  },
  in_progress: {
    label: "In Progress",
    bg: "#EFF6FF",
    text: "#1D4ED8",
    dot: "#3B82F6",
    isPulsing: true,
  },
  pending: {
    label: "Pending",
    bg: "#FFFBEB",
    text: "#B45309",
    dot: "#F59E0B",
  },
  blocked: {
    label: "Blocked",
    bg: "#FEF2F2",
    text: "#B91C1C",
    dot: "#EF4444",
  },
  cancelled: {
    label: "Cancelled",
    bg: "#F3F4F6",
    text: "#4B5563",
    dot: "#9CA3AF",
  },
};

export const METHOD_CONFIG: Record<HttpMethod, { bg: string; text: string; border: string }> = {
  GET: { bg: "#E0F2FE", text: "#0369A1", border: "#BAE6FD" },
  POST: { bg: "#F0FDF4", text: "#15803D", border: "#BBF7D0" },
  PUT: { bg: "#FEF3C7", text: "#B45309", border: "#FDE68A" },
  PATCH: { bg: "#F3E8FF", text: "#6B21A8", border: "#E9D5FF" },
  DELETE: { bg: "#FEE2E2", text: "#B91C1C", border: "#FCA5A5" },
};

export const PRIORITY_CONFIG: Record<Priority, { label: string; bg: string; text: string; border: string }> = {
  urgent: { label: "Urgent", bg: "#FEF2F2", text: "#991B1B", border: "#FCA5A5" },
  high: { label: "High", bg: "#FFF7ED", text: "#C2410C", border: "#FDBA74" },
  medium: { label: "Medium", bg: "#F0F9FF", text: "#0369A1", border: "#BAE6FD" },
  low: { label: "Low", bg: "#F8FAFC", text: "#475569", border: "#E2E8F0" },
};

export const ModernStatusBadge: React.FC<{ status: RequestStatus; size?: "small" | "medium" }> = ({
  status,
  size = "small",
}) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const isSmall = size === "small";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: isSmall ? 1.25 : 1.75,
        py: isSmall ? 0.4 : 0.6,
        borderRadius: "20px",
        backgroundColor: config.bg,
        color: config.text,
        fontWeight: 600,
        fontSize: isSmall ? "0.75rem" : "0.85rem",
        lineHeight: 1.2,
        letterSpacing: "0.02em",
        border: `1px solid ${config.dot}25`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
        whiteSpace: "nowrap",
      }}
    >
      <Box
        sx={{
          width: isSmall ? 7 : 9,
          height: isSmall ? 7 : 9,
          borderRadius: "50%",
          backgroundColor: config.dot,
          boxShadow: `0 0 0 2px ${config.dot}33`,
          animation: config.isPulsing ? "pulseDot 1.8s infinite ease-in-out" : "none",
          "@keyframes pulseDot": {
            "0%": { transform: "scale(0.95)", boxShadow: `0 0 0 0 ${config.dot}70` },
            "70%": { transform: "scale(1)", boxShadow: `0 0 0 6px ${config.dot}00` },
            "100%": { transform: "scale(0.95)", boxShadow: `0 0 0 0 ${config.dot}00` },
          },
        }}
      />
      {config.label}
    </Box>
  );
};

export const ModernMethodChip: React.FC<{ method?: HttpMethod | string }> = ({ method }) => {
  if (!method) return null;
  const m = method.toUpperCase() as HttpMethod;
  const config = METHOD_CONFIG[m] || { bg: "#F3F4F6", text: "#374151", border: "#E5E7EB" };

  return (
    <Chip
      label={m}
      size="small"
      sx={{
        fontFamily: "'Fira Code', 'Roboto Mono', monospace",
        fontWeight: 700,
        fontSize: "0.7rem",
        height: 22,
        borderRadius: "6px",
        backgroundColor: config.bg,
        color: config.text,
        border: `1px solid ${config.border}`,
        letterSpacing: "0.5px",
        px: 0.5,
      }}
    />
  );
};

export const ModernPriorityChip: React.FC<{ priority: Priority }> = ({ priority }) => {
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.2,
        py: 0.3,
        borderRadius: "8px",
        backgroundColor: config.bg,
        color: config.text,
        fontWeight: 600,
        fontSize: "0.75rem",
        border: `1px solid ${config.border}`,
      }}
    >
      {config.label}
    </Box>
  );
};

interface ModernStatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  colorBg?: string;
  accentColor?: string;
  subtext?: string;
}

export const ModernStatCard: React.FC<ModernStatCardProps> = ({
  title,
  value,
  icon,
  accentColor = "#3B82F6",
  subtext,
}) => {
  return (
    <Card
      sx={{
        borderRadius: "16px",
        border: "1px solid rgba(226, 232, 240, 0.8)",
        boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.05), 0 2px 6px -1px rgba(0, 0, 0, 0.02)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        position: "relative",
        overflow: "hidden",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 12px 28px -4px rgba(0, 0, 0, 0.08), 0 4px 12px -2px rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          backgroundColor: accentColor,
        }}
      />
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontWeight: 600, fontSize: "0.85rem", mb: 0.5 }}
            >
              {title}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: "text.primary", letterSpacing: "-0.5px" }}>
              {value}
            </Typography>
            {subtext && (
              <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5, display: "block" }}>
                {subtext}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: "14px",
              backgroundColor: `${accentColor}12`,
              color: accentColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 2px 8px ${accentColor}20`,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const ModernSearchBar: React.FC<{
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder = "Search requests..." }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: "4px 12px",
        display: "flex",
        alignItems: "center",
        borderRadius: "24px",
        border: "1px solid rgba(203, 213, 225, 0.8)",
        backgroundColor: "background.paper",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          borderColor: "rgba(148, 163, 184, 0.9)",
        },
        "&:focus-within": {
          borderColor: "#3B82F6",
          boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.15)",
        },
      }}
    >
      <SearchIcon sx={{ color: "text.secondary", mr: 1, fontSize: 20 }} />
      <InputBase
        sx={{ ml: 0.5, flex: 1, fontSize: "0.875rem" }}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </Paper>
  );
};
